import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import './FitText.css';

export interface FitTextProps {
  children: ReactNode;
  /**
   * The text, for the character estimate, when `children` is not a plain
   * string. Defaults to the string children.
   */
  text?: string;
  /** Largest size in px. Default: whatever the surrounding text role gives. */
  max?: number;
  /** Smallest size in px. Default: a third of the largest, never under 14. */
  min?: number;
  /** How many characters fit at the largest size before shrinking starts. Default 6. */
  comfortable?: number;
  className?: string;
  style?: CSSProperties;
  as?: 'span' | 'div';
}

/**
 * The size a string should be drawn at, from its length alone: the estimate
 * used before anything has been measured, and wherever nothing can be.
 *
 * Six characters (a four-digit count, "R 450") fit at full size; past that the
 * size falls in proportion, so "R 12 345,67" at 11 characters draws at about
 * half, and a date at 12 to 15 characters a little under that. The floor keeps
 * a very long value legible rather than tiny.
 */
export function estimateFitSize(chars: number, max: number, min: number, comfortable = 6): number {
  if (chars <= comfortable) return max;
  return Math.max(min, Math.min(max, Math.floor((max * comfortable) / chars)));
}

/**
 * One-line text that shrinks to fit its container.
 *
 * A KPI value is drawn large, and large works for "1 248" or "19". It does not
 * work for "R 1 234 567,89" or "05 September 2026", which either wrapped to a
 * second line or ran under an ellipsis, and a figure with its last digits cut
 * off is a wrong figure. This draws the value at the size the surrounding
 * text role gives, or `max`, measures it against the width it has, and scales
 * it down, never up, until it fits on one line, down to `min`. The default
 * floor is a third of the ceiling: a 60px display figure may fall to 20px,
 * which is still a headline in a card, and an eleven-character amount fits a
 * four-card row at 1100px there where half would have clipped it.
 *
 * The first paint already uses an estimate from the character count, so a
 * long value never flashes oversized before the measurement corrects it.
 * A resize re-measures, so a card that narrows keeps its figure whole.
 */
export function FitText({ children, text, max, min, comfortable = 6, className, style, as = 'span' }: FitTextProps): ReactNode {
  const outer = useRef<HTMLElement | null>(null);
  const inner = useRef<HTMLElement | null>(null);
  const label = text ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : '');
  const estimate = useRef<number | null>(null);
  const [size, setSize] = useState<number | null>(() => {
    // Before a measurement the only facts are the length and, when given, the
    // bounds. Without `max` the role's size is unknown here, so the first
    // render inherits it and the layout effect below corrects before paint.
    if (max == null) return null;
    const floor = min ?? Math.max(14, Math.round(max / 3));
    const guess = estimateFitSize(label.length, max, floor, comfortable);
    estimate.current = guess < max ? guess : null;
    return estimate.current;
  });

  useLayoutEffect(() => {
    const box = outer.current;
    const el = inner.current;
    if (!box || !el) return;

    const fit = () => {
      // Measure at the ceiling: the role's own size, or `max`.
      el.style.fontSize = max != null ? `${max}px` : '';
      const base = parseFloat(getComputedStyle(el).fontSize) || max || 16;
      const floor = min ?? Math.max(14, Math.round(base / 3));
      const available = box.clientWidth;
      const needed = el.scrollWidth;
      if (!available || !needed) {
        // No layout to measure against (a test renderer, a hidden tab): the
        // character estimate stands.
        el.style.fontSize = estimate.current != null ? `${estimate.current}px` : (max != null ? `${max}px` : '');
        return;
      }
      const next = needed <= available
        ? null
        : Math.max(floor, Math.floor((base * available) / needed * 0.985));
      el.style.fontSize = next == null ? (max != null ? `${max}px` : '') : `${next}px`;
      setSize((prev) => (prev === next ? prev : next));
    };

    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [label, children, max, min, comfortable]);

  const Outer = as;
  return (
    <Outer ref={outer as never} className={['rr-fit-text', className].filter(Boolean).join(' ')} style={style}>
      <span ref={inner as never} className="rr-fit-text__inner" style={size != null ? { fontSize: size } : max != null ? { fontSize: max } : undefined}>
        {children}
      </span>
    </Outer>
  );
}
